import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, TextField, Typography } from "@material-ui/core";
import { useDispatch } from "react-redux";
import SignatureCapture from "../SignatureCapture/SignatureCapture";
import { updateJobRequest } from "../../actions/job";
import toaster from "../../utils/toaster";

/**
 * Staff UI to capture Bill of Lading customer acknowledgment; stored on the job and shown on Customer Copy PDFs.
 */
const BolSignaturePanel = ({ job }) => {
  const dispatch = useDispatch();
  const sigRef = useRef(null);
  const [signerName, setSignerName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const n = job && `${job.consigneeF || ""} ${job.consigneeL || ""}`.trim();
    setSignerName((prev) => (prev.trim() ? prev : n || ""));
  }, [job?.consigneeF, job?.consigneeL]);

  if (!job || !job.gcnno) {
    return null;
  }

  const hasSig =
    job.bolSignaturePng && String(job.bolSignaturePng).startsWith("data:image/png");

  const onSave = () => {
    const sig = sigRef.current?.getDataUrl?.() || "";
    const name = signerName.trim();
    if (name.length < 2) {
      toaster("Enter the signer name (at least 2 characters).", { type: "error" });
      return;
    }
    if (!sig.startsWith("data:image/png;base64,")) {
      toaster("Draw a signature in the box.", { type: "error" });
      return;
    }
    setSaving(true);
    dispatch(updateJobRequest({ bolSignaturePng: sig, bolSignerName: name }, job.gcnno));
    window.setTimeout(() => setSaving(false), 700);
  };

  return (
    <Paper style={{ padding: 16, marginBottom: 16, maxWidth: 520 }}>
      <Typography variant="h6" gutterBottom>
        Bill of Lading — customer signature
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Saves to this job and appears on the Customer Copy pages of the job PDF.
      </Typography>
      {hasSig ? (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Current saved signature
          </Typography>
          <img
            alt="BOL signature"
            src={job.bolSignaturePng}
            style={{ maxWidth: 240, border: "1px solid #ddd", display: "block" }}
          />
          <Typography variant="body2">{job.bolSignerName}</Typography>
          {job.bolSignedAt ? (
            <Typography variant="caption" display="block" color="textSecondary">
              {new Date(job.bolSignedAt).toLocaleString()}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        label="Signer name"
        value={signerName}
        onChange={(e) => setSignerName(e.target.value)}
      />
      <Box mt={2}>
        <SignatureCapture
          ref={sigRef}
          height={140}
          label="Customer signature (in-person / on behalf of customer)"
        />
      </Box>
      <Box mt={2}>
        <Button variant="contained" color="primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : hasSig ? "Replace signature" : "Save signature"}
        </Button>
      </Box>
    </Paper>
  );
};

export default BolSignaturePanel;
